import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import type {
  McpTool,
  ToolExecutionRequest,
  ToolExecutionResponse,
  ToolRegistry,
  WebSearchRequest,
  WebSearchResult,
  LocalSearchRequest,
  LocalSearchResult,
  CalculatorRequest,
  CalculatorResult,
  VectorStoreResult,
  TelegramBotResult,
  ToolAnalytics,
  ToolExecution,
  McpServerInfo,
} from '../types/mcp';

class McpApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL || '';

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 60000, // Longer timeout for tool executions
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const session = this.getStoredSession();
        console.log('🔍 McpApiService - Request interceptor debug:', {
          url: config.url,
          hasSession: !!session,
          hasSessionId: !!session?.sessionId,
          sessionIdLength: session?.sessionId?.length || 0,
        });

        if (session?.sessionId) {
          config.headers.Authorization = `Bearer ${session.sessionId}`;
          console.log(
            '🔍 McpApiService - Added Authorization header:',
            config.headers.Authorization
          );
        } else {
          console.log('🔍 McpApiService - No session or sessionId, skipping Authorization header');
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          // Handle authentication errors
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        return Promise.reject(error);
      }
    );
  }

  private getStoredSession() {
    try {
      console.log('🔍 McpApiService - getStoredSession called');
      
      const sessionData = localStorage.getItem('mcp_session');
      console.log('🔍 McpApiService - Raw localStorage data:', sessionData);
      console.log('🔍 McpApiService - localStorage keys:', Object.keys(localStorage));
      
      let session = null;
      
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          console.log('🔍 McpApiService - Parsed data:', parsed);
          
          // Handle both wrapped format (from storage service) and unwrapped format (from auth-api)
          if (parsed && typeof parsed === 'object' && 'data' in parsed) {
            // Wrapped format: { data: {...}, timestamp: ..., version: ... }
            session = parsed.data;
            console.log('🔍 McpApiService - Using wrapped format, extracted session:', session);
          } else {
            // Unwrapped format: direct session object
            session = parsed;
            console.log('🔍 McpApiService - Using unwrapped format, session:', session);
          }
        } catch (parseError) {
          console.error('Error parsing session data:', parseError);
        }
      } else {
        console.log('🔍 McpApiService - No session data found in localStorage');
        
        // Check if there are other session-related keys
        const allKeys = Object.keys(localStorage);
        const sessionKeys = allKeys.filter(key => key.includes('session') || key.includes('auth') || key.includes('token'));
        console.log('🔍 McpApiService - Other session-related keys found:', sessionKeys);
        
        // Try to get session from other possible keys
        for (const key of sessionKeys) {
          const value = localStorage.getItem(key);
          console.log(`🔍 McpApiService - Key "${key}" value:`, value);
        }
      }
      
      console.log('🔍 McpApiService - Final session object:', session);
      console.log('🔍 McpApiService - Session sessionId:', session?.sessionId);
      console.log('🔍 McpApiService - Session type:', typeof session?.sessionId);
      console.log('🔍 McpApiService - Session length:', session?.sessionId?.length);
      
      return session;
    } catch {
      console.error('🔍 McpApiService - Error parsing session data');
      return null;
    }
  }

  // Tool Registry Management
  async getToolRegistry(): Promise<ToolRegistry> {
    try {
      const response: AxiosResponse<ToolRegistry> = await this.client.get('/api/tools');
      return response.data;
    } catch (error: any) {
      console.error('Get tool registry error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch tool registry');
    }
  }

  async getTool(toolName: string): Promise<McpTool> {
    try {
      const response: AxiosResponse<McpTool> = await this.client.get(`/api/tools/${toolName}`);
      return response.data;
    } catch (error: any) {
      console.error('Get tool error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch tool information');
    }
  }

  // Generic Tool Execution
  async executeTool(request: ToolExecutionRequest): Promise<ToolExecutionResponse> {
    try {
      const response: AxiosResponse<ToolExecutionResponse> = await this.client.post(
        '/api/tools/execute',
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Tool execution error:', error);

      // Return error response in expected format
      return {
        success: false,
        error: {
          type: 'server',
          code: 'EXECUTION_FAILED',
          message: error.response?.data?.message || 'Tool execution failed',
          retryable: error.response?.status >= 500,
        },
        executionId: `error_${Date.now()}`,
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString(),
        duration: 0,
      };
    }
  }

  // Web Search Tools
  async webSearch(request: WebSearchRequest): Promise<WebSearchResult[]> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'profikid-remote_web_search',
        arguments: request,
        options: { timeout: 30000 },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'json' && content.text) {
          const data = JSON.parse(content.text);
          return data.results || [];
        }
      }

      return [];
    } catch (error: any) {
      console.error('Web search error:', error);
      throw new Error('Web search failed');
    }
  }

  async localSearch(request: LocalSearchRequest): Promise<LocalSearchResult[]> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'local_search',
        arguments: request,
        options: { timeout: 30000 },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'json' && content.text) {
          const data = JSON.parse(content.text);
          return data.results || [];
        }
      }

      return [];
    } catch (error: any) {
      console.error('Local search error:', error);
      throw new Error('Local search failed');
    }
  }

  // Calculator Tools
  async calculate(request: CalculatorRequest): Promise<CalculatorResult> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'calculate',
        arguments: {
          operation: request.operation,
          a: request.operands[0],
          b: request.operands[1],
        },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'text' && content.text) {
          const result = parseFloat(content.text);
          return {
            result,
            expression: `${request.operands[0]} ${request.operation} ${request.operands[1]}`,
            unit: request.unit,
          };
        }
      }

      throw new Error('Calculation failed');
    } catch (error: any) {
      console.error('Calculate error:', error);
      throw new Error('Calculation failed');
    }
  }

  async add(a: number, b: number): Promise<number> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'add',
        arguments: { a, b },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'text' && content.text) {
          return parseFloat(content.text);
        }
      }

      throw new Error('Addition failed');
    } catch (error: any) {
      console.error('Add error:', error);
      throw new Error('Addition failed');
    }
  }

  // Vector Store Tools
  async vectorStoreInsert(
    content: string,
    metadata?: Record<string, any>
  ): Promise<VectorStoreResult> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'vector_store',
        arguments: {
          action: 'insert',
          content,
          metadata,
          contentType: 'text',
        },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'json' && content.text) {
          return JSON.parse(content.text);
        }
      }

      return { success: false };
    } catch (error: any) {
      console.error('Vector store insert error:', error);
      throw new Error('Vector store insert failed');
    }
  }

  async vectorStoreQuery(query: string, topK = 5): Promise<VectorStoreResult> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'vector_store',
        arguments: {
          action: 'query',
          content: query,
          topK,
        },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'json' && content.text) {
          return JSON.parse(content.text);
        }
      }

      return { success: false, documents: [] };
    } catch (error: any) {
      console.error('Vector store query error:', error);
      throw new Error('Vector store query failed');
    }
  }

  // Telegram Bot Tools
  async telegramSendMessage(
    chatId: string,
    text: string,
    parseMode?: string
  ): Promise<TelegramBotResult> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'telegram_bot',
        arguments: {
          action: 'send_message',
          chat_id: chatId,
          text,
          parse_mode: parseMode,
        },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'json' && content.text) {
          return JSON.parse(content.text);
        }
      }

      return { success: false };
    } catch (error: any) {
      console.error('Telegram send message error:', error);
      throw new Error('Failed to send Telegram message');
    }
  }

  async telegramBroadcast(message: string): Promise<TelegramBotResult> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'telegram_bot',
        arguments: {
          action: 'broadcast_notification',
          notification_text: message,
        },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'json' && content.text) {
          return JSON.parse(content.text);
        }
      }

      return { success: false };
    } catch (error: any) {
      console.error('Telegram broadcast error:', error);
      throw new Error('Failed to broadcast message');
    }
  }

  async telegramGetSubscribers(): Promise<any[]> {
    try {
      const toolRequest: ToolExecutionRequest = {
        toolName: 'telegram_bot',
        arguments: {
          action: 'get_subscribers',
        },
      };

      const response = await this.executeTool(toolRequest);

      if (response.success && response.result?.content) {
        const content = response.result.content[0];
        if (content.type === 'json' && content.text) {
          const data = JSON.parse(content.text);
          return data.subscribers || [];
        }
      }

      return [];
    } catch (error: any) {
      console.error('Get Telegram subscribers error:', error);
      throw new Error('Failed to get subscribers');
    }
  }

  // Tool Analytics and History
  async getToolExecutionHistory(limit = 50, offset = 0): Promise<ToolExecution[]> {
    try {
      const response: AxiosResponse<{ data: ToolExecution[] }> = await this.client.get(
        `/api/tools/history?limit=${limit}&offset=${offset}`
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Get tool history error:', error);
      return [];
    }
  }

  async getToolAnalytics(toolName?: string): Promise<ToolAnalytics[]> {
    try {
      const url = toolName ? `/api/tools/analytics/${toolName}` : '/api/tools/analytics';
      const response: AxiosResponse<{ data: ToolAnalytics[] }> = await this.client.get(url);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Get tool analytics error:', error);
      return [];
    }
  }

  async getExecutionById(executionId: string): Promise<ToolExecution | null> {
    try {
      const response: AxiosResponse<{ data: ToolExecution }> = await this.client.get(
        `/api/tools/executions/${executionId}`
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Get execution error:', error);
      return null;
    }
  }

  // Server Information
  async getServerInfo(): Promise<McpServerInfo> {
    try {
      const response: AxiosResponse<McpServerInfo> = await this.client.get('/api/server/info');
      return response.data;
    } catch (error: any) {
      console.error('Get server info error:', error);
      return {
        name: 'MCP Tools Server',
        version: '1.0.0',
        description: 'Model Context Protocol Tools Collection',
        capabilities: [],
        tools: [],
        health: {
          status: 'unhealthy',
          uptime: 0,
          lastCheck: new Date().toISOString(),
        },
        statistics: {
          totalRequests: 0,
          activeConnections: 0,
          averageResponseTime: 0,
          errorRate: 0,
          throughput: 0,
          memoryUsage: 0,
          cpuUsage: 0,
        },
      };
    }
  }

  async getServerHealth(): Promise<{ status: string; uptime: number; issues?: any[] }> {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error: any) {
      console.error('Health check error:', error);
      return { status: 'unhealthy', uptime: 0 };
    }
  }

  // Bulk operations
  async executeBulkTools(requests: ToolExecutionRequest[]): Promise<ToolExecutionResponse[]> {
    try {
      const response: AxiosResponse<{ results: ToolExecutionResponse[] }> = await this.client.post(
        '/api/tools/bulk-execute',
        { requests }
      );
      return response.data.results || [];
    } catch (error: any) {
      console.error('Bulk execution error:', error);
      throw new Error('Bulk tool execution failed');
    }
  }

  // Tool validation
  async validateToolInput(
    toolName: string,
    input: Record<string, any>
  ): Promise<{ valid: boolean; errors?: string[] }> {
    try {
      const response = await this.client.post('/api/tools/validate', {
        toolName,
        input,
      });
      return response.data;
    } catch (error: any) {
      console.error('Tool validation error:', error);
      return {
        valid: false,
        errors: ['Validation service unavailable'],
      };
    }
  }

  // Tool suggestions based on input
  async suggestTools(query: string): Promise<McpTool[]> {
    try {
      const response: AxiosResponse<{ suggestions: McpTool[] }> = await this.client.post(
        '/api/tools/suggest',
        { query }
      );
      return response.data.suggestions || [];
    } catch (error: any) {
      console.error('Tool suggestion error:', error);
      return [];
    }
  }

  // Export tool execution results
  async exportExecutionResults(
    executionIds: string[],
    format: 'json' | 'csv' = 'json'
  ): Promise<Blob> {
    try {
      const response = await this.client.post(
        '/api/tools/export',
        {
          executionIds,
          format,
        },
        {
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Export results error:', error);
      throw new Error('Failed to export results');
    }
  }

  // Real-time tool execution status via Server-Sent Events
  createExecutionEventSource(executionId: string): EventSource | null {
    try {
      const session = this.getStoredSession();
      const url = new URL(`${this.baseURL}/api/tools/executions/${executionId}/events`);

      if (session?.sessionId) {
        url.searchParams.set('token', session.sessionId);
      }

      return new EventSource(url.toString());
    } catch (error) {
      console.error('Failed to create event source:', error);
      return null;
    }
  }

  // Cancel tool execution
  async cancelExecution(executionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.client.post(`/api/tools/executions/${executionId}/cancel`);
      return response.data;
    } catch (error: any) {
      console.error('Cancel execution error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to cancel execution',
      };
    }
  }

  // Get tool usage statistics for current user
  async getUserToolUsage(): Promise<{ toolName: string; count: number; lastUsed: string }[]> {
    try {
      console.log('🔍 McpApiService - getUserToolUsage - Making request to /api/user/tool-usage');
      const response = await this.client.get('/api/user/tool-usage');
      console.log('🔍 McpApiService - getUserToolUsage - Response received:', response.status);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Get user tool usage error:', error);
      return [];
    }
  }

  // Save tool execution as template
  async saveExecutionAsTemplate(
    executionId: string,
    name: string,
    description: string
  ): Promise<{ success: boolean; templateId?: string }> {
    try {
      const response = await this.client.post('/api/tools/templates', {
        executionId,
        name,
        description,
      });
      return response.data;
    } catch (error: any) {
      console.error('Save template error:', error);
      return { success: false };
    }
  }

  // Get user's saved templates
  async getUserTemplates(): Promise<any[]> {
    try {
      const response = await this.client.get('/api/user/templates');
      return response.data.data || [];
    } catch (error: any) {
      console.error('Get user templates error:', error);
      return [];
    }
  }
}

// Create singleton instance
export const mcpApi = new McpApiService();
